<template>
  <!-- Loading -->
  <mc-loading-indicator
    v-if="showSpinner"
    class="card card--loading"
    variant="spinner"
    appearance="primary"
    fit="large"
    :label="$t('common.loading')"
  />

  <div v-else data-test="ocean-shipments" class="ocean-shipments">
    <div class="mb-4">{{ $t("shipments.description") }}</div>

    <!-- Filters -->
    <div data-test="ocean-shipments-tab-filters" class="table-filters">
      <mc-select
        :label="`${isExport ? $t('common.departingIn') : $t('common.arrivingIn')}:`"
        name="timeFrames"
        :value="timeFrame"
        labelposition="left"
        class="mr-4"
        @optionselected="updateTimeFrame"
      >
        <mc-option v-for="opt in timeframeOptions" :key="opt.value" :value="opt.value">{{ opt.label }}</mc-option>
      </mc-select>
      <JsonToCSV :filterValue="timeFrame" view-name="Shipments" :bookings="shipments" :pageNumber="paginationToken" />
    </div>

    <!-- No results -->
    <MarketingNotification
      v-if="shipments && shipments.length === 0 && !shipmentsError && !showSpinner"
      class="card"
      data-test="no-results-ocean"
    />

    <!-- Error -->
    <mc-notification
      v-else-if="shipments && shipments.length === 0 && !showSpinner"
      class="card"
      appearance="error"
      icon="exclamation-circle"
      width="fit-content"
      data-test="error-ocean"
      :body="$t('datatable.error.noRecords500')"
    />

    <!-- Results -->
    <mc-table
      v-else
      fit="small"
      sortdisabled
      :data.prop="shipments"
      :columns.prop="[
        {
          id: 'blNumber',
          label: $t('common.blNumber'),
          subDataLabel: $t('tables.references'),
          verticalAlign: 'top',
          width: '175px'
        },
        {
          id: 'from',
          label: $t('common.from'),
          subDataLabel: $t('tables.depPort'),
          verticalAlign: 'top'
        },
        {
          id: 'to',
          label: $t('common.to'),
          subDataLabel: $t('tables.arrPort'),
          verticalAlign: 'top'
        },
        {
          id: 'vessel',
          label: $t('common.vessel'),
          verticalAlign: 'top'
        },
        {
          id: 'cargo',
          label: $t('common.cargo'),
          verticalAlign: 'top',
          width: '175px'
        }
      ]"
      data-test="shipments-table"
    >
      <template v-for="item in shipments" :key="item.id">
        <div :slot="`${item.id}_blNumber`">
          <a
            data-test="document-table-shipment-link"
            :href="`/shipment-details/${item.ladingNumber}`"
            @click="trackShipmentClick('shipments', isExport)"
          >
            <b>{{ item.ladingNumber }}</b>
          </a>
          <p v-if="item.bookedByRef" class="color-neutral-subtle mds-text--small-normal wrap-anywhere">
            {{ $t("common.bookedByRef") }},
            {{ item.bookedByRef }}
          </p>
        </div>
        <div :slot="`${item.id}_from`">
          <FromTo :place="item?.placeOfReceipt || item?.portOfLoading" :port="item?.portOfLoading" />
        </div>
        <div :slot="`${item.id}_to`">
          <FromTo
            :place="item?.placeOfDelivery || item?.portOfDischarge"
            :port="item?.portOfDischarge"
            type="arrival"
          />
        </div>
        <div :slot="`${item.id}_vessel`">
          <Vessel :vessel="item" />
        </div>
        <div :slot="`${item.id}_cargo`">
          <Cargo
            :commodity="item?.cargo?.commodity"
            :packageQuantity="item?.cargo?.containerQuantity"
            :packageQuantityUnit="item?.cargo?.containerName"
          />
        </div>
      </template>
    </mc-table>

    <Pagination
      v-if="shipments && shipments.length > 0 && lastPage > 1"
      activeView="shipments"
      :lastPage="lastPage"
      :totalCount="totalCount"
      :currentPage="paginationToken"
      @update-data-source="getData"
    />
  </div>
</template>
<script>
import Pagination from "@/components/shared/Pagination.vue";
import { mapActions, mapState, mapWritableState } from "pinia";
import { shipments } from "@/store/shipments";
import { filters } from "@/store/filters";
import MarketingNotification from "@/components/shared/MarketingNotification.vue";
import { getExportImportPage, trackShipmentClick } from "@/lib/utilities";
import JsonToCSV from "@/components/shared/JsonToCSV.vue";
import FromTo from "@/components/shared/FromTo.vue";
import Cargo from "@/components/shared/Cargo.vue";
import Vessel from "@/components/shared/Vessel.vue";
import "@maersk-global/mds-components-core/mc-table";

export default {
  name: "Shipments",
  components: { JsonToCSV, MarketingNotification, Pagination, FromTo, Cargo, Vessel },
  data() {
    return {
      timeframeOptions: [
        { label: this.$t("timings.next3days"), value: "next_3_days" },
        { label: this.$t("timings.next7days"), value: "next_7_days" },
        { label: this.$t("timings.nextMonth"), value: "next_month" },
        { label: this.$t("timings.next3months"), value: "next_3_months" },
        { label: this.$t("timings.last3days"), value: "last_3_days" },
        { label: this.$t("timings.last7days"), value: "last_7_days" },
        { label: this.$t("timings.lastMonth"), value: "last_month" },
        { label: this.$t("timings.last3months"), value: "last_3_months" }
      ]
    };
  },
  computed: {
    ...mapState(shipments, [
      "lastPage",
      "shipments",
      "shipmentsError",
      "totalCount",
      "showSpinner",
      "timeFrame",
      "paginationToken"
    ]),
    ...mapWritableState(shipments, ["timeFrame", "paginationToken"]),
    ...mapState(filters, ["activeFilter"]),
    isExport() {
      return this.$route?.path?.toLowerCase()?.includes?.("export");
    }
  },
  watch: {
    activeFilter: {
      async handler() {
        await this.updatePaginationToken(1);
        await this.getBookings();
      },
      deep: true
    },
    timeFrame: {
      handler: async function (newValue, oldValue) {
        if (newValue !== oldValue) {
          await this.updatePaginationToken(1);
          await this.getBookings();
          this.$sendGAEvent({
            event: "UXEvent",
            action: `${getExportImportPage()} Shipments QF`,
            category: "UX_ShipmentMngt",
            label: `${
              this.isExport ? this.$t("common.departingIn") : this.$t("common.arrivingIn")
            }:|${this.timeFrame.replaceAll("_", " ")}`
          });
          this.$sendGAEvent({
            event: "DQCS",
            action: "Click",
            category: "SO_QuickFilter",
            label: {
              data: "Shipments quick filter",
              view: getExportImportPage(),
              departingOrArriving: `${this.isExport ? this.$t("common.departingIn") : this.$t("common.arrivingIn")}:`,
              timeframe: this.timeFrame.replaceAll("_", " ")
            }
          });
        }
      }
    }
  },
  async mounted() {
    if (this.$route.query?.filter) {
      this.timeFrame = this.$route.query.filter;
      await this.updatePaginationToken(1);
    }
    if (this.shipments.length === 0) {
      await this.getBookings();
    }
  },

  methods: {
    trackShipmentClick,
    ...mapActions(shipments, ["getBookings", "updateTimeFrame", "updatePaginationToken"]),
    async getData(page) {
      if (page === this.paginationToken) return;
      await this.updatePaginationToken(page);
      await this.getBookings();
    }
  }
};
</script>
